<template>
  <v-dialog v-model="internalValue" max-width="1280px" persistent scrollable>
    <v-card class="rounded-xl border-light bg-white">
      <!-- Modal Header -->
      <v-card-title class="d-flex align-center justify-space-between py-3 px-5 border-light-bottom bg-white">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2">
            {{ form.EccID ? 'mdi-file-document-edit-outline' : 'mdi-file-document-plus-outline' }}
          </v-icon>
          <div>
            <span class="text-h6 font-weight-black grey--text text--darken-4">
              {{ form.EccID ? `Editar Contrato Comercial: ${form.EccContractNumber || ''}` : 'Nuevo Contrato Comercial' }}
            </span>
            <span class="ml-2">
              <v-chip small :color="getStatusColor(form.EccStatus)" class="font-weight-bold" text-color="white">
                {{ getStatusName(form.EccStatus) }}
              </v-chip>
            </span>
          </div>
        </div>
        <v-btn icon color="grey darken-2" small @click="closeModal">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Modal Body con 3 Secciones Simplificadas -->
      <v-card-text class="pt-4 pb-4 px-5 bg-white">
        <v-form ref="formRef" v-model="valid">
          <v-tabs v-model="activeTab" color="primary" class="mb-4">
            <v-tab class="text-none font-weight-bold">
              <v-icon left small>mdi-card-text-outline</v-icon>
              1. Datos del Contrato
            </v-tab>
            <v-tab class="text-none font-weight-bold">
              <v-icon left small>mdi-package-variant-closed</v-icon>
              2. Productos Contratados ({{ form.Items.length }})
            </v-tab>
            <v-tab class="text-none font-weight-bold">
              <v-icon left small>mdi-file-pdf-box</v-icon>
              3. Documento Contractual
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="activeTab">
            <!-- ========================================================= -->
            <!-- SECCIÓN 1: DATOS DEL CONTRATO -->
            <!-- ========================================================= -->
            <v-tab-item>
              <v-card flat class="pa-1">
                <div class="text-subtitle-2 font-weight-bold grey--text text--darken-4 text-uppercase mb-3">
                  <v-icon small color="grey darken-3" class="mr-1">mdi-clipboard-outline</v-icon>
                  Información Principal del Acuerdo
                </div>

                <!-- Campos Principales e Indispensables -->
                <v-row dense>
                  <v-col cols="12" sm="6" md="3">
                    <s-text
                      label="N° Contrato / Purchase Agreement *"
                      v-model="form.EccContractNumber"
                      placeholder="ej: 48769"
                      :error="submitted && !form.EccContractNumber"
                    />
                  </v-col>

                  <v-col cols="12" sm="6" md="5">
                    <sselect-client-sap
                      label="Cliente Comercial (SAP) *"
                      return-object
                      v-model="selectedCustomer"
                      :text="form.EccCustomerName"
                      @input="onClientSelected"
                      :error="submitted && !form.PrsCustomerID"
                    />
                  </v-col>

                  <v-col cols="12" sm="6" md="2">
                    <s-select-definition
                      v-model="form.TypeDestination"
                      :def="1175"
                      label="Destino / División *"
                      clearable
                      :error="submitted && !form.TypeDestination"
                    />
                  </v-col>

                  <v-col cols="12" sm="6" md="2">
                    <s-select-campaign
                      label="Campaña *"
                      v-model="form.CmpID"
                      clearable
                      :error="submitted && form.EccStatus === 2 && !form.CmpID"
                    />
                  </v-col>

                  <v-col cols="12" sm="6" md="3">
                    <s-date
                      label="Fecha del Acuerdo *"
                      v-model="form.EccAgreementDate"
                      :error="submitted && form.EccStatus === 2 && !form.EccAgreementDate"
                    />
                  </v-col>

                  <v-col cols="12" sm="6" md="3">
                    <s-date
                      label="Inicio Vigencia"
                      v-model="form.EccValidityStartDate"
                      clearable
                      placeholder="dd/mm/aaaa"
                    />
                  </v-col>

                  <v-col cols="12" sm="6" md="3">
                    <s-date
                      label="Fin Vigencia"
                      v-model="form.EccValidityEndDate"
                      clearable
                      placeholder="dd/mm/aaaa"
                    />
                  </v-col>

                  <v-col cols="12" sm="6" md="3">
                    <s-select-definition
                      v-model="form.TypeCurrency"
                      :def="1129"
                      label="Moneda *"
                      clearable
                      :error="submitted && form.EccStatus === 2 && !form.TypeCurrency"
                    />
                  </v-col>

                  <v-col cols="12" sm="6" md="3">
                    <s-select-definition
                      v-model="form.TypeIncoterm"
                      :def="1742"
                      label="Incoterm *"
                      clearable
                      add
                    />
                  </v-col>

                  <v-col cols="12" sm="6" md="9">
                    <s-text
                      label="Condición de Pago *"
                      v-model="form.EccPaymentCondition"
                      placeholder="ej: Cash Against Documents"
                      :error="submitted && form.EccStatus === 2 && !form.EccPaymentCondition"
                    />
                  </v-col>
                </v-row>

                <!-- Panel Contraíble: Datos Adicionales -->
                <v-expansion-panels flat class="mt-3 border-light rounded-xl">
                  <v-expansion-panel class="rounded-xl">
                    <v-expansion-panel-header class="py-2 px-4 bg-grey-lighten-5 font-weight-bold text-caption grey--text text--darken-3">
                      <div class="d-flex align-center">
                        <v-icon small color="primary" class="mr-2">mdi-tune-variant</v-icon>
                        Datos Adicionales (Comprador Legal, Proveedor, Ejecutivo, Lugar Incoterm, Observaciones)
                      </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="pt-3 px-2 bg-white">
                      <v-row dense>
                        <v-col cols="12" sm="6">
                          <s-text
                            label="Comprador o Razón Social Legal"
                            v-model="form.EccBuyerLegalName"
                            placeholder="ej: Atalanta Corporation"
                          />
                        </v-col>

                        <v-col cols="12" sm="6">
                          <s-text
                            label="Proveedor"
                            v-model="form.EccProviderName"
                          />
                        </v-col>

                        <v-col cols="12" sm="6">
                          <s-text
                            label="Ejecutivo Comercial"
                            v-model="form.EccCommercialExecutive"
                          />
                        </v-col>

                        <v-col cols="12" sm="6">
                          <s-text
                            label="Lugar Incoterm"
                            v-model="form.EccIncotermPlace"
                            placeholder="ej: PEPAI (Puerto Paita)"
                          />
                        </v-col>

                        <v-col cols="12">
                          <s-text
                            label="Observaciones Generales"
                            v-model="form.EccObservation"
                            placeholder="Notas o cláusulas contractuales adicionales..."
                          />
                        </v-col>
                      </v-row>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-card>
            </v-tab-item>

            <!-- ========================================================= -->
            <!-- SECCIÓN 2: PRODUCTOS CONTRATADOS -->
            <!-- ========================================================= -->
            <v-tab-item>
              <v-card flat class="pa-1">
                <div class="d-flex align-center justify-space-between mb-3 flex-wrap">
                  <div class="d-flex align-center">
                    <v-icon small color="grey darken-3" class="mr-1">mdi-package-variant-closed</v-icon>
                    <span class="text-subtitle-2 font-weight-bold grey--text text--darken-4 text-uppercase">
                      Líneas de Productos Contractuales
                    </span>
                  </div>
                  <v-btn
                    color="primary"
                    small
                    class="rounded-lg text-none font-weight-bold px-3"
                    @click="addNewItem"
                    :disabled="!isEditable"
                  >
                    <v-icon left small>mdi-plus</v-icon>
                    Agregar Producto
                  </v-btn>
                </div>

                <!-- Tarjetas / Formulario de Productos Simplificado -->
                <div v-if="form.Items.length === 0" class="text-center py-6 grey--text">
                  <v-icon large color="grey lighten-1">mdi-package-variant</v-icon>
                  <div class="mt-2">No se han registrado productos en el contrato. Haga clic en "Agregar Producto".</div>
                </div>

                <v-card
                  v-for="(item, index) in form.Items"
                  :key="index"
                  outlined
                  class="mb-4 rounded-xl border-light pa-3 bg-grey-lighten-5"
                >
                  <div class="d-flex align-center justify-space-between mb-2">
                    <span class="text-caption font-weight-black text-uppercase primary--text">
                      Línea #{{ index + 1 }}: {{ item.EciProductName || 'Nuevo Producto' }}
                    </span>
                    <div class="d-flex align-center">
                      <v-btn
                        icon
                        small
                        color="primary"
                        @click="duplicateItem(index)"
                        :disabled="!isEditable"
                        title="Duplicar este producto"
                        class="mr-1"
                      >
                        <v-icon small>mdi-content-copy</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        small
                        color="error"
                        @click="removeItem(index)"
                        :disabled="!isEditable || form.Items.length <= 1"
                        title="Eliminar línea"
                      >
                        <v-icon small>mdi-trash-can-outline</v-icon>
                      </v-btn>
                    </div>
                  </div>

                  <!-- Fila Directa Visible: Campos Indispensables con Definiciones -->
                  <v-row dense>
                    <v-col cols="12" md="4">
                      <s-text
                        label="Producto *"
                        v-model="item.EciProductName"
                        placeholder="ej: Blueberries IQF Organic Unsized"
                        @input="onProductNameInput(item)"
                      />
                    </v-col>

                    <v-col cols="12" md="2">
                      <s-text
                        label="Presentación *"
                        v-model="item.EciPresentation"
                        placeholder="ej: 30 lb/caja"
                        @input="onPresentationInput(item)"
                      />
                    </v-col>

                    <v-col cols="12" sm="6" md="2">
                      <s-text
                        label="Cantidad Contratada *"
                        v-model.number="item.EciContractedQuantity"
                        number
                        @input="recalculateItem(item)"
                      />
                    </v-col>

                    <v-col cols="12" sm="6" md="2">
                      <s-select-definition
                        v-model="item.TypeQuantityUnit"
                        :def="1743"
                        label="Unidad Cantidad *"
                        clearable
                        add
                      />
                    </v-col>

                    <v-col cols="12" sm="6" md="2">
                      <s-text
                        label="KG Declarados (PDF) *"
                        v-model.number="item.EciDeclaredKg"
                        number
                        @input="recalculateItem(item)"
                      />
                    </v-col>

                    <v-col cols="12" sm="6" md="2">
                      <s-text
                        label="Precio Unitario *"
                        v-model.number="item.EciUnitPrice"
                        number
                        @input="recalculateItem(item)"
                      />
                    </v-col>

                    <v-col cols="12" sm="6" md="2">
                      <s-select-definition
                        v-model="item.TypePriceUnit"
                        :def="1744"
                        label="Unidad Precio *"
                        clearable
                        add
                        @input="recalculateItem(item)"
                      />
                    </v-col>

                    <v-col cols="12" sm="12" md="4">
                      <s-text
                        label="Importe Contractual Declarado *"
                        v-model.number="item.EciContractAmount"
                        number
                      />
                    </v-col>
                  </v-row>

                  <!-- Panel Contraíble por Línea: Especificaciones y Condiciones Adicionales -->
                  <v-expansion-panels flat class="mt-2 border-light rounded-xl">
                    <v-expansion-panel class="rounded-xl">
                      <v-expansion-panel-header class="py-1 px-3 bg-white font-weight-bold text-caption grey--text text--darken-3">
                        <div class="d-flex align-center">
                          <v-icon small color="grey darken-2" class="mr-2">mdi-cog-outline</v-icon>
                          Especificaciones y condiciones adicionales (Variedad, Empaque, Peso caja, Contenedor, Paletización, Requisitos)
                        </div>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content class="pt-2 px-1 bg-white">
                        <v-row dense>
                          <v-col cols="12" md="6">
                            <s-text
                              label="Descripción Contractual Detallada"
                              v-model="item.EciContractDescription"
                              placeholder="ej: Blueberries IQF Organic Unsized"
                            />
                          </v-col>
                          <v-col cols="12" sm="6" md="3">
                            <s-text
                              label="Cód. Producto Cliente"
                              v-model="item.EciCustomerProductCode"
                              placeholder="ej: P10529"
                            />
                          </v-col>
                          <v-col cols="12" sm="6" md="3">
                            <s-text
                              label="Cód. Interno / SQ"
                              v-model="item.EciInternalProductCode"
                            />
                          </v-col>

                          <v-col cols="12" sm="6" md="3">
                            <s-text
                              label="Variedad"
                              v-model="item.EciVariety"
                            />
                          </v-col>
                          <v-col cols="12" sm="6" md="3">
                            <s-text
                              label="Certificación"
                              v-model="item.EciCertification"
                              placeholder="ej: Organic / GlobalGAP"
                            />
                          </v-col>
                          <v-col cols="12" sm="6" md="3">
                            <s-text
                              label="Tipo de Empaque"
                              v-model="item.EciPackingType"
                              placeholder="ej: Caja"
                            />
                          </v-col>
                          <v-col cols="12" sm="6" md="3">
                            <s-text
                              label="Peso Caja (kg)"
                              v-model.number="item.EciWeightPerBoxKg"
                              number
                              @input="recalculateItem(item)"
                            />
                          </v-col>

                          <v-col cols="12" sm="6" md="4">
                            <s-text
                              label="Tipo Contenedor"
                              v-model="item.EciContainerType"
                              placeholder="ej: Reefer High Cube de 40 pies"
                            />
                          </v-col>
                          <v-col cols="12" sm="6" md="4">
                            <s-text
                              label="Paletización"
                              v-model="item.EciPalletizationRequirement"
                              placeholder="ej: GMA"
                            />
                          </v-col>
                          <v-col cols="12" sm="12" md="4">
                            <s-text
                              label="Lead Time / Requisitos Especiales"
                              v-model="item.EciSpecialRequirements"
                              placeholder="ej: Lead time: 7 días hábiles"
                            />
                          </v-col>
                        </v-row>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>

                  <!-- Strip Comparativo Discreto de KG y Valores Calculados -->
                  <div class="d-flex align-center justify-space-between mt-2 pt-2 border-light-top flex-wrap text-caption">
                    <div>
                      <span class="grey--text text--darken-2">KG Declarados:</span>
                      <strong class="ml-1">{{ item.EciDeclaredKg | formatNum }} kg</strong>
                    </div>
                    <div>
                      <span class="grey--text text--darken-2">KG Calculados (Cant × Peso):</span>
                      <strong class="ml-1">{{ item.EciCalculatedKg | formatNum }} kg</strong>
                    </div>
                    <div class="d-flex align-center">
                      <span class="grey--text text--darken-2 mr-1">Diferencia:</span>
                      <!-- Si es diferencia por redondeo menor o igual a 1 kg -->
                      <span
                        v-if="Math.abs(item.EciKgDifference) <= 1.0"
                        class="grey--text text--darken-3 font-weight-bold"
                      >
                        <v-icon x-small color="grey darken-2" class="mr-1">mdi-information-outline</v-icon>
                        Redondeo: {{ item.EciKgDifference | formatNum }} kg
                      </span>
                      <!-- Si la diferencia supera 1 kg -->
                      <v-chip
                        v-else
                        x-small
                        color="warning"
                        class="font-weight-bold"
                        text-color="white"
                      >
                        {{ item.EciKgDifference | formatNum }} kg
                      </v-chip>
                    </div>
                    <div>
                      <span class="grey--text text--darken-2">Importe Línea:</span>
                      <strong class="ml-1 primary--text text--darken-2">{{ item.EciContractAmount | formatCurrency }}</strong>
                    </div>
                  </div>
                </v-card>
              </v-card>
            </v-tab-item>

            <!-- ========================================================= -->
            <!-- SECCIÓN 3: DOCUMENTO CONTRACTUAL (PDF) -->
            <!-- ========================================================= -->
            <v-tab-item>
              <v-card flat class="pa-1">
                <div class="text-subtitle-2 font-weight-bold grey--text text--darken-4 text-uppercase mb-3">
                  <v-icon small color="grey darken-3" class="mr-1">mdi-file-pdf-box</v-icon>
                  Adjunto del Contrato Original (PDF)
                </div>

                <v-row dense>
                  <v-col cols="12" md="6">
                    <v-file-input
                      v-model="pdfFile"
                      accept="application/pdf"
                      label="Seleccionar Contrato en PDF"
                      outlined
                      dense
                      prepend-icon="mdi-paperclip"
                      :disabled="!isEditable"
                      show-size
                      truncate-length="25"
                      @change="onFileSelected"
                      class="mt-4"
                    />
                  </v-col>

                  <v-col cols="12" md="6" class="d-flex align-center">
                    <v-btn
                      v-if="form.EccFileName"
                      color="primary"
                      outlined
                      class="mr-2 rounded-lg text-none font-weight-bold"
                      @click="previewContractPdf"
                    >
                      <v-icon left small>mdi-eye</v-icon>
                      Visualizar PDF
                    </v-btn>

                    <v-btn
                      v-if="form.EccFileName"
                      color="grey darken-3"
                      outlined
                      class="rounded-lg text-none font-weight-bold"
                      @click="downloadContractPdf"
                    >
                      <v-icon left small>mdi-download</v-icon>
                      Descargar PDF
                    </v-btn>
                  </v-col>
                </v-row>

                <!-- Metadata del Archivo -->
                <v-card v-if="form.EccFileName" outlined class="pa-3 rounded-lg border-light mt-3 bg-grey-lighten-5">
                  <div class="text-caption font-weight-bold grey--text text--darken-3 text-uppercase mb-2">
                    Información del Documento Guardado:
                  </div>
                  <v-row dense class="text-caption">
                    <v-col cols="12" sm="4">
                      <strong>Nombre Archivo:</strong> {{ form.EccFileOriginalName || form.EccFileName }}
                    </v-col>
                    <v-col cols="12" sm="4">
                      <strong>Tamaño:</strong> {{ formatBytes(form.EccFileSize) }}
                    </v-col>
                    <v-col cols="12" sm="4">
                      <strong>Fecha de Carga:</strong> {{ form.EccFileUploadDate ? $fun.formatDateTimeView(form.EccFileUploadDate) : 'Reciente' }}
                    </v-col>
                  </v-row>
                </v-card>
              </v-card>
            </v-tab-item>
          </v-tabs-items>

          <!-- Resumen Fijo Inferior Compacto -->
          <v-divider class="my-3" />
          <v-row dense class="bg-grey-lighten-5 rounded-lg pa-3 align-center border-light">
            <v-col cols="6" sm="3" class="text-center">
              <span class="caption font-weight-bold grey--text text--darken-2 text-uppercase">Productos:</span>
              <div class="text-subtitle-1 font-weight-black grey--text text--darken-4">
                {{ form.Items.length }}
              </div>
            </v-col>
            <v-col cols="6" sm="3" class="text-center">
              <span class="caption font-weight-bold grey--text text--darken-2 text-uppercase">Total Cantidad:</span>
              <div class="text-subtitle-1 font-weight-black grey--text text--darken-4">
                {{ totalContractQuantity | formatNum }}
              </div>
            </v-col>
            <v-col cols="6" sm="3" class="text-center">
              <span class="caption font-weight-bold grey--text text--darken-2 text-uppercase">Total KG Declarados:</span>
              <div class="text-subtitle-1 font-weight-black grey--text text--darken-4">
                {{ totalContractKg | formatNum }} kg
              </div>
            </v-col>
            <v-col cols="6" sm="3" class="text-center">
              <span class="caption font-weight-bold grey--text text--darken-2 text-uppercase">Importe Total:</span>
              <div class="text-h6 font-weight-black primary--text text--darken-4">
                {{ totalContractAmount | formatCurrency }}
              </div>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <!-- Modal Footer con Acciones Simplificadas (Guardar Borrador / Guardar Contrato) -->
      <v-card-actions class="px-5 pb-4 pt-2 border-light-top bg-white">
        <v-btn text class="rounded-lg px-4 text-none font-weight-bold" @click="closeModal" :disabled="saving">
          Cancelar
        </v-btn>
        <v-spacer />
        <v-btn
          outlined
          color="grey darken-3"
          class="rounded-lg px-4 font-weight-bold text-none mr-2"
          @click="submitContract(1)"
          :loading="saving"
          :disabled="!isEditable"
        >
          <v-icon left small>mdi-file-edit-outline</v-icon>
          Guardar Borrador
        </v-btn>
        <v-btn
          color="primary"
          class="rounded-lg px-5 font-weight-bold text-none"
          @click="submitContract(2)"
          :loading="saving"
          :disabled="!isEditable"
          elevation="0"
        >
          <v-icon left small>mdi-content-save-check</v-icon>
          Guardar Contrato
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Modal Visor PDF Embebido -->
    <v-dialog v-model="pdfDialog.show" max-width="900px" scrollable>
      <v-card class="rounded-xl">
        <v-card-title class="d-flex align-center justify-space-between py-3 px-4 border-light-bottom">
          <span class="text-subtitle-1 font-weight-bold grey--text text--darken-4">
            Vista Previa del Contrato Original
          </span>
          <v-btn icon small @click="pdfDialog.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0" style="height: 600px;">
          <iframe
            v-if="pdfDialog.url"
            :src="pdfDialog.url"
            width="100%"
            height="100%"
            style="border: none;"
          ></iframe>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import SselectClientSap from "@/components/Sale/SselectClientSap.vue";
import SSelectCampaign from "@/components/Utils/SSelectCampaign.vue";
import SSelectDefinition from "@/components/Utils/SSelectDefinition.vue";
import SDate from "@/components/Utils/SDate.vue";
import SText from "@/components/Utils/SText.vue";
import _sCommercialContract from "@/services/ExternalTrade/CommercialContractService.js";
import _GenUploadFiles from "@/services/General/GenUploadFiles.js";

export default {
  name: "CommercialContractFormModal",
  components: {
    SselectClientSap,
    SSelectCampaign,
    SSelectDefinition,
    SDate,
    SText,
  },
  filters: {
    formatNum(val) {
      if (val === null || val === undefined || isNaN(val)) return "0";
      return new Intl.NumberFormat("es-PE", { maximumFractionDigits: 2 }).format(val);
    },
    formatCurrency(val) {
      if (val === null || val === undefined || isNaN(val)) return "0.00";
      return new Intl.NumberFormat("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val);
    },
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    contractId: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      valid: true,
      submitted: false,
      saving: false,
      activeTab: 0,
      pdfFile: null,
      pdfDialog: {
        show: false,
        url: null,
      },
      selectedCustomer: null,
      form: {
        EccID: 0,
        EccContractNumber: "",
        TypeDestination: 2, // Def 1175: 1: Frescos, 2: IQF (Congelados)
        CmpID: null,
        PrsCustomerID: null,
        EccCustomerName: "",
        EccBuyerLegalName: "",
        EccProviderName: "Sunshine Export SAC",
        EccAgreementDate: new Date().toISOString().substr(0, 10),
        EccValidityStartDate: null,
        EccValidityEndDate: null,
        TypeCurrency: 2, // Def 1129: 2: Dólares (USD)
        TypeIncoterm: 1, // Def 1742: 1: FOB
        EccIncotermPlace: "PEPAI",
        EccPaymentCondition: "Cash Against Documents",
        EccCommercialExecutive: "",
        EccObservation: "",
        EccFileName: "",
        EccFileOriginalName: "",
        EccFileExtension: ".pdf",
        EccFileSize: null,
        EccFileUploadDate: null,
        EccFileUploadUser: null,
        EccStatus: 1, // 1: Borrador, 2: Registrado
        SecStatus: 1,
        Items: [],
      },
    };
  },
  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    isEditable() {
      return !this.form.EccID || this.form.EccStatus !== 3;
    },
    totalContractQuantity() {
      return (this.form.Items || []).reduce((acc, item) => acc + (Number(item.EciContractedQuantity) || 0), 0);
    },
    totalContractKg() {
      return (this.form.Items || []).reduce((acc, item) => acc + (Number(item.EciDeclaredKg) || 0), 0);
    },
    totalContractAmount() {
      return (this.form.Items || []).reduce((acc, item) => acc + (Number(item.EciContractAmount) || 0), 0);
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.initForm();
      }
    },
  },
  methods: {
    getStatusName(status) {
      const map = { 1: "Borrador", 2: "Registrado", 3: "Anulado", 4: "Cerrado" };
      return map[status] || "Borrador";
    },
    getStatusColor(status) {
      const map = { 1: "grey darken-1", 2: "primary", 3: "error", 4: "teal" };
      return map[status] || "grey";
    },
    formatBytes(bytes) {
      if (!bytes) return "-";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },
    onClientSelected(val) {
      if (val && typeof val === "object") {
        this.form.PrsCustomerID = val.CardCode || "";
        this.form.EccCustomerName = val.CardName || "";
        if (!this.form.EccBuyerLegalName) {
          this.form.EccBuyerLegalName = val.CardName || "";
        }
      } else if (val && typeof val === "string") {
        this.form.PrsCustomerID = val;
      } else {
        this.form.PrsCustomerID = null;
        this.form.EccCustomerName = "";
      }
    },
    onProductNameInput(item) {
      if (!item.EciContractDescription) {
        item.EciContractDescription = item.EciProductName;
      }
    },
    onPresentationInput(item) {
      if (item.EciPresentation && (!item.EciWeightPerBoxKg || item.EciWeightPerBoxKg === 1)) {
        const lower = item.EciPresentation.toLowerCase();
        if (lower.includes("30") && lower.includes("lb")) {
          item.EciWeightPerBoxKg = 13.6078;
        } else if (lower.includes("10") && lower.includes("lb")) {
          item.EciWeightPerBoxKg = 4.5359;
        } else if (lower.includes("20") && lower.includes("lb")) {
          item.EciWeightPerBoxKg = 9.0718;
        }
        this.recalculateItem(item);
      }
    },
    initForm() {
      this.submitted = false;
      this.activeTab = 0;
      this.pdfFile = null;
      this.selectedCustomer = null;

      const defaultUserName = (this.$fun && this.$fun.getUserName && this.$fun.getUserName()) || "";

      if (this.contractId && this.contractId > 0) {
        this.loadContract(this.contractId);
      } else {
        this.form = {
          EccID: 0,
          EccContractNumber: "",
          TypeDestination: 2, // Def 1175
          CmpID: null,
          PrsCustomerID: null,
          EccCustomerName: "",
          EccBuyerLegalName: "",
          EccProviderName: "Sunshine Export SAC",
          EccAgreementDate: new Date().toISOString().substr(0, 10),
          EccValidityStartDate: null,
          EccValidityEndDate: null,
          TypeCurrency: 2, // Def 1129
          TypeIncoterm: 1, // Def 1742
          EccIncotermPlace: "PEPAI",
          EccPaymentCondition: "Cash Against Documents",
          EccCommercialExecutive: defaultUserName,
          EccObservation: "",
          EccFileName: "",
          EccFileOriginalName: "",
          EccFileExtension: ".pdf",
          EccFileSize: null,
          EccFileUploadDate: null,
          EccFileUploadUser: null,
          EccStatus: 1,
          SecStatus: 1,
          Items: [],
        };
        this.addNewItem();
      }
    },
    async loadContract(id) {
      try {
        const resp = await _sCommercialContract.getById(id, this.$fun.getUserID());
        if (resp && resp.status === 200 && resp.data) {
          const data = resp.data;
          this.form = {
            ...data,
            EccAgreementDate: data.EccAgreementDate ? String(data.EccAgreementDate).split("T")[0] : null,
            EccValidityStartDate: data.EccValidityStartDate ? String(data.EccValidityStartDate).split("T")[0] : null,
            EccValidityEndDate: data.EccValidityEndDate ? String(data.EccValidityEndDate).split("T")[0] : null,
            Items: (data.Items || []).map((item) => ({
              ...item,
              Schedules: (data.Schedules || []).filter((s) => s.EciID === item.EciID),
            })),
          };

          if (data.PrsCustomerID) {
            this.selectedCustomer = {
              CardCode: data.PrsCustomerID,
              CardName: data.EccCustomerName,
            };
          } else {
            this.selectedCustomer = null;
          }

          if (this.form.Items.length === 0) {
            this.addNewItem();
          }
        }
      } catch (err) {
        console.error("Error al cargar contrato:", err);
        this.$fun.alert("Error al cargar el detalle del contrato comercial.", "error");
      }
    },
    addNewItem() {
      this.form.Items.push({
        EciID: 0,
        EccID: this.form.EccID,
        EciCustomerProductCode: "P10529",
        EciInternalProductCode: "",
        EciProductLine: "Berries",
        EciProductName: "Blueberries IQF Organic Unsized",
        EciContractDescription: "Blueberries IQF Organic Unsized",
        EciVariety: "",
        EciCertification: "Organic",
        EciPresentation: "30 lb/caja",
        EciPackingType: "Caja",
        EciWeightPerBoxKg: 13.6078,
        EciContractedQuantity: 12800,
        TypeQuantityUnit: 1, // Def 1743: 1: Cajas
        EciDeclaredKg: 174179,
        EciCalculatedKg: 174179.84,
        EciKgDifference: 0.84,
        EciUnitPrice: 4.10,
        TypePriceUnit: 1, // Def 1744: 1: USD/kg
        EciContractAmount: 714137.34,
        EciContainerType: "Reefer High Cube de 40 pies",
        EciPalletizationRequirement: "GMA",
        EciSpecialRequirements: "Lead time: 7 días hábiles",
        SecStatus: 1,
        Schedules: [],
      });
    },
    removeItem(index) {
      if (this.form.Items.length <= 1) return;
      this.form.Items.splice(index, 1);
    },
    duplicateItem(index) {
      if (!this.form.Items || !this.form.Items[index]) return;
      const source = this.form.Items[index];
      const cloned = {
        ...JSON.parse(JSON.stringify(source)),
        EciID: 0,
        Schedules: (source.Schedules || []).map((s) => ({
          ...s,
          EcsID: 0,
          EciID: 0,
        })),
      };
      this.form.Items.splice(index + 1, 0, cloned);
      this.$fun.alert("Producto duplicado correctamente.", "success");
    },
    recalculateItem(item) {
      const qty = Number(item.EciContractedQuantity) || 0;
      const weight = Number(item.EciWeightPerBoxKg) || 0;
      const declared = Number(item.EciDeclaredKg) || 0;
      const price = Number(item.EciUnitPrice) || 0;

      const calcKg = Math.round(qty * weight * 100) / 100;
      item.EciCalculatedKg = calcKg;
      item.EciKgDifference = Math.round((declared - calcKg) * 100) / 100;

      if (!item.EciContractAmount || item.EciContractAmount <= 0) {
        if (item.TypePriceUnit === 1) {
          item.EciContractAmount = Math.round(declared * price * 100) / 100;
        } else {
          item.EciContractAmount = Math.round(qty * price * 100) / 100;
        }
      }
    },
    async onFileSelected(file) {
      if (!file) return;
      if (file.type !== "application/pdf" && !file.name.endsWith(".pdf")) {
        this.$fun.alert("Solo se aceptan archivos en formato PDF.", "warning");
        this.pdfFile = null;
        return;
      }

      try {
        const fileName = `Contract_${this.form.EccContractNumber || 'Draft'}_${Date.now()}`;
        const resp = await _GenUploadFiles.upload(file, fileName, "RutaAnexosSAP", true);

        if (resp && resp.status === 200) {
          this.form.EccFileName = fileName;
          this.form.EccFileOriginalName = file.name;
          this.form.EccFileExtension = ".pdf";
          this.form.EccFileSize = file.size;
          this.form.EccFileUploadDate = new Date().toISOString();
          this.form.EccFileUploadUser = this.$fun.getUserID();
          this.$fun.alert("Documento PDF adjuntado correctamente.", "success");
        }
      } catch (err) {
        console.error("Error al subir PDF:", err);
        this.$fun.alert("Error al cargar el archivo PDF.", "error");
      }
    },
    async previewContractPdf() {
      if (!this.form.EccFileName) return;
      try {
        const fileObj = await _GenUploadFiles.getFileForPreview("RutaAnexosSAP", this.form.EccFileName);
        if (fileObj && fileObj.url) {
          this.pdfDialog.url = fileObj.url;
          this.pdfDialog.show = true;
        }
      } catch (err) {
        console.error("Error al previsualizar:", err);
        this.$fun.alert("No se pudo previsualizar el PDF.", "error");
      }
    },
    downloadContractPdf() {
      if (!this.form.EccFileName) return;
      _GenUploadFiles.getFile("RutaAnexosSAP", this.form.EccFileName);
    },
    closeModal() {
      this.internalValue = false;
    },
    submitContract(targetStatus) {
      this.submitted = true;
      this.form.EccStatus = targetStatus;

      // 1. Validaciones mínimas obligatorias para ambos estados
      if (!this.form.EccContractNumber) {
        this.$fun.alert("Debe ingresar el N° de Contrato o Purchase Agreement.", "warning");
        this.activeTab = 0;
        return;
      }
      if (!this.form.PrsCustomerID) {
        this.$fun.alert("Debe seleccionar un Cliente Comercial SAP.", "warning");
        this.activeTab = 0;
        return;
      }
      if (!this.form.TypeDestination) {
        this.$fun.alert("Debe seleccionar el Destino / División.", "warning");
        this.activeTab = 0;
        return;
      }

      // 2. Validaciones estrictas si se desea guardar como Contrato Completo (Estado 2)
      if (targetStatus === 2) {
        if (!this.form.CmpID) {
          this.$fun.alert("Debe seleccionar la Campaña para registrar el contrato.", "warning");
          this.activeTab = 0;
          return;
        }
        if (!this.form.EccAgreementDate) {
          this.$fun.alert("Debe indicar la Fecha del Acuerdo.", "warning");
          this.activeTab = 0;
          return;
        }
        if (this.form.EccValidityStartDate && this.form.EccValidityEndDate && this.form.EccValidityStartDate > this.form.EccValidityEndDate) {
          this.$fun.alert("La fecha final de vigencia no puede ser menor que la inicial.", "warning");
          this.activeTab = 0;
          return;
        }
        if (!this.form.TypeCurrency) {
          this.$fun.alert("Debe indicar la Moneda del contrato.", "warning");
          this.activeTab = 0;
          return;
        }
        if (!this.form.EccPaymentCondition) {
          this.$fun.alert("Debe indicar la Condición de Pago.", "warning");
          this.activeTab = 0;
          return;
        }
        if (this.form.Items.length === 0) {
          this.$fun.alert("Debe ingresar por lo menos un producto contratado.", "warning");
          this.activeTab = 1;
          return;
        }

        const invalidItem = this.form.Items.find(
          (i) => !i.EciProductName || !i.EciContractedQuantity || i.EciContractedQuantity <= 0 || !i.EciUnitPrice || i.EciUnitPrice <= 0
        );
        if (invalidItem) {
          this.$fun.alert("Todos los productos deben tener nombre, cantidad mayor a 0 y precio mayor a 0.", "warning");
          this.activeTab = 1;
          return;
        }
      }

      const actionText = targetStatus === 1 ? "guardar como Borrador" : "guardar y registrar el Contrato Comercial";
      this.$fun.alert(`¿Está seguro de ${actionText}?`, "question").then((r) => {
        if (r && r.value) {
          this.saving = true;

          const payload = {
            ...this.form,
            Items: this.form.Items.map((item) => ({
              ...item,
              Schedules: item.Schedules || [],
            })),
          };

          _sCommercialContract
            .save(payload, this.$fun.getUserID())
            .then(() => {
              const successMsg = targetStatus === 1 ? "Borrador guardado correctamente." : "Contrato comercial registrado correctamente.";
              this.$fun.alert(successMsg, "success");
              this.$emit("saved");
              this.closeModal();
            })
            .catch((err) => {
              console.error("Error al guardar contrato:", err);
              const msg = (err.response && err.response.data && err.response.data.Message) || err.message || "Ocurrió un error al guardar.";
              this.$fun.alert(msg, "error");
            })
            .finally(() => {
              this.saving = false;
            });
        }
      });
    },
  },
};
</script>

<style scoped>
.border-light {
  border: 1px solid #e2e8f0 !important;
}

.border-light-bottom {
  border-bottom: 1px solid #e2e8f0 !important;
}

.border-light-top {
  border-top: 1px solid #e2e8f0 !important;
}

.bg-grey-lighten-5 {
  background-color: #f8fafc !important;
}

.border-rounded {
  border-radius: 8px;
  overflow: hidden;
}
</style>
